import { forwardRef, useEffect, useState } from "react";
import { Loader } from "@progress/kendo-react-indicators";
import { useDispatch } from "react-redux";

import { addErrorsAction } from "actions/errors";
import { getUserName } from "authProvider";
import { Button, ButtonMode } from "components/Button/Button";
import { IconType } from "components/Icon/Icon";
import TruLink from "components/TruLink/TruLink";
import { parseServiceErrors } from "services/encounter/serviceErrors";
import { versionAPI } from "services/version";
import { getVersionLabel } from "utils/version";

import "./UserMenu.scss";

import type { FocusEvent, MouseEvent, RefObject } from "react";
import type { To } from "react-router";
import type { ApiVersion } from "models/apiVersion";
import type { UserState } from "models/user";

// These variables are set during the build time depending on the environment.
// DO NOT SET THEM IN .env* files.
const BRANCH_NAME = process.env.REACT_APP_BRANCH_NAME ?? "local";
const GIT_COMMIT_HASH = process.env.REACT_APP_GIT_COMMIT_HASH ?? "local";

interface UserMenuProps {
  changePassword: () => void;
  closeMenuPopup: () => void;
  encounterOpened: boolean;
  hasChangesCallback?: (to: To, clickHandler?: () => void) => void;
  isErrorMode?: boolean;
  logout: () => void;
  user: UserState;
}

const UserMenu = forwardRef<HTMLUListElement, UserMenuProps>(
  (
    {
      changePassword,
      closeMenuPopup,
      encounterOpened,
      hasChangesCallback,
      isErrorMode = false,
      logout,
      user,
    },
    ref,
  ) => {
    const [isClientModeVisible, setIsClientModeVisible] = useState(false);
    const [isVersionLoading, setIsVersionLoading] = useState(false);
    const [serverVersion, setServerVersion] = useState<ApiVersion | null>(null);
    const dispatch = useDispatch();

    const showClientMode = (event: MouseEvent<HTMLLIElement>) => {
      // FIXME: This does not work on MacOS due to the default behaviour
      if (event.ctrlKey) {
        setIsClientModeVisible(true);
      }
    };

    const handleChangePassword = () => {
      closeMenuPopup();
      changePassword();
    };

    const handleLogout = () => {
      closeMenuPopup();
      logout();
    };

    const onBlur = (event: FocusEvent) => {
      if (
        !event.relatedTarget ||
        !(ref as RefObject<HTMLUListElement>).current?.contains(event.relatedTarget)
      ) {
        closeMenuPopup();
      }
    };

    const name =
      user.account.firstName !== "" && user.account.lastName !== "" ?
        `${user.account.firstName} ${user.account.lastName}`
      : (getUserName() as string);

    useEffect(() => {
      const fetchApiVersion = async () => {
        setIsVersionLoading(true);
        try {
          const response = await versionAPI.getApiVersion();
          setServerVersion(response);
        } catch (error) {
          const errors = parseServiceErrors(error as Error, "Failed to get server version");
          dispatch(addErrorsAction(errors));
        } finally {
          setIsVersionLoading(false);
        }
      };

      fetchApiVersion();
    }, [dispatch]);

    return (
      <ul className="tc-appbar-menu right" onBlur={onBlur} ref={ref} tabIndex={0}>
        <li className="info">
          <strong>{name}</strong>
        </li>
        <li className="separator" />
        <li>
          <TruLink
            encounterOpened={encounterOpened}
            hasChangesCallback={hasChangesCallback}
            isErrorMode={isErrorMode}
            to="/user-preferences"
          >
            <Button
              className="user-external-button"
              iconPosition="right"
              iconType={encounterOpened ? IconType.ExternalLink : undefined}
              label="Personal Settings"
              mode={ButtonMode.Transparent}
              onClick={closeMenuPopup}
            />
          </TruLink>
        </li>
        <li>
          <Button
            className="user-external-button"
            data-testid="change-password-button"
            iconPosition="right"
            iconType={encounterOpened ? IconType.ExternalLink : undefined}
            label="Change Password"
            mode={ButtonMode.Transparent}
            onClick={handleChangePassword}
          />
        </li>
        <li className="separator" />
        <li>
          <Button
            data-testid="logout-button"
            label="Log out"
            mode={ButtonMode.Transparent}
            onClick={handleLogout}
          />
        </li>
        <li className="separator" />
        <li className="revision" onClick={showClientMode} data-testid="client-version-info">
          Client Version: v{getVersionLabel(BRANCH_NAME)} (
          <span className="version" data-testid="client-version-info-git" title={GIT_COMMIT_HASH}>
            {GIT_COMMIT_HASH.substring(0, 8)}
          </span>
          )
        </li>
        <li className="revision" onClick={showClientMode} data-testid="server-version-info">
          Server Version:{" "}
          {isVersionLoading && (
            <Loader className="revision-loader" type="converging-spinner" size="small" />
          )}
          {!isVersionLoading && serverVersion && (
            <>
              v{getVersionLabel(serverVersion.GitBranchName)} (
              <span
                className="version"
                data-testid="server-version-info-git"
                title={serverVersion.GitCommitHash}
              >
                {serverVersion.GitCommitHash.substring(0, 8)}
              </span>
              )
            </>
          )}
        </li>
        <li className={`client-mode show-${isClientModeVisible}`}>{user.clientMode}</li>
      </ul>
    );
  },
);

export default UserMenu;
